import { PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { FieldArray, FormikProvider } from 'formik';
import { useFormik } from 'components/form/formik';
import { useEffect, useState } from 'react';

import { Button } from 'components/button/Button';
import { Card } from 'components/Card';
import { InputField } from 'components/input/InputField';
import { AutocompletePostalcode } from 'src/app/location/components/AutocompletePostalcode';
import { TrailerTypes } from 'src/app/order/pages/order/TrailerTypes';
import { QuotationPriceInput } from '../../components/QuotationPriceInput';
import { CustomerComboboxField } from 'src/app/customer/CustomerComboboxField';
import { createRoute } from '@utils/mapbox-api.client';
import { serializeCoordinates } from '@utils/mapbox-api';

import { useAIDraftCtx } from './context';
import { initialLineValues, ILineState, IQuotationLineValues } from '../types';

export const ReviewLanesStep = () => {
  const { state, dispatch } = useAIDraftCtx();
  const [, setLineStates] = useState<ILineState[]>(
    state.lanes.map(() => ({
      departure: undefined,
      arrival: undefined,
      fetchKey: Date.now().toString(),
    })),
  );

  const formikbag = useFormik({
    initialValues: {
      customer: state.customer,
      lines: state.lanes,
    },
    onSubmit: () => {},
  });

  const { values, setFieldValue } = formikbag;

  // Sync form values back to reducer state when moving forward
  useEffect(() => {
    dispatch({ type: 'SET_LANES', lanes: values.lines });
  }, [values.lines]);

  useEffect(() => {
    if (values.customer) {
      dispatch({ type: 'SET_CUSTOMER', customer: values.customer });
    }
  }, [values.customer]);

  const fetchRoute = async (lineState: ILineState, idx: number) => {
    if (!lineState.departure || !lineState.arrival) {
      return;
    }

    const fetchKey = `${serializeCoordinates(lineState.departure)}-${serializeCoordinates(lineState.arrival)}`;
    if (lineState.fetchKey === fetchKey) {
      return;
    }

    setLineStates((prev) => prev.map((line, i) => (i === idx ? { ...line, fetchKey } : line)));

    const response = await createRoute(lineState.departure, lineState.arrival);
    if (response) {
      setLineStates((prev) =>
        prev.map((line, i) => {
          if (line.fetchKey === fetchKey) {
            setFieldValue(`lines[${i}].distance`, (response.distance / 1000).toFixed(2));
          }
          return line;
        }),
      );
    }
  };

  return (
    <div className="px-4">
      <FormikProvider value={formikbag}>
        <div className="mb-8">
          <Card title="Klant">
            <CustomerComboboxField name="customer" />
          </Card>
        </div>

        <FieldArray
          name="lines"
          render={(arrayHelpers) => (
            <div className="my-4">
              <div className="flex justify-between items-end mb-4">
                <h2 className="heading-two">Lijnen ({values.lines.length})</h2>
                <Button
                  onTrigger={() => {
                    arrayHelpers.push({ ...initialLineValues });
                    setLineStates((prev) => [
                      ...prev,
                      {
                        departure: undefined,
                        arrival: undefined,
                        fetchKey: Date.now().toString(),
                      },
                    ]);
                  }}
                  iconLeft={<PlusIcon className="button-icon" />}
                >
                  Voeg lijn toe
                </Button>
              </div>

              {values.lines.length > 0 ? (
                <div className="grid gap-4">
                  {values.lines.map((_line: IQuotationLineValues, idx: number) => (
                    <div className="card p-4" key={`lane-${idx}`}>
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="heading-three">Lijn {idx + 1}</h3>
                        <Button
                          color="danger"
                          onTrigger={() => {
                            arrayHelpers.remove(idx);
                            setLineStates((prev) => prev.filter((_, i) => i !== idx));
                          }}
                          iconLeft={<TrashIcon className="button-icon" />}
                        >
                          Verwijder
                        </Button>
                      </div>

                      <div className="flex w-full gap-4">
                        <div className="grid quotation-line-form-columns gap-4 w-full">
                          <div>
                            <div className="font-medium">Van</div>
                            <AutocompletePostalcode
                              postalCodeName={`lines[${idx}].departurePostalCode`}
                              countryName={`lines[${idx}].departureCountry`}
                              cityName={`lines[${idx}].departureCity`}
                              onAutocomplete={(_postalCode, _city, longitude, latitude) => {
                                setLineStates((prev) => {
                                  prev[idx].departure = [longitude, latitude];
                                  fetchRoute(prev[idx], idx).catch(console.error);
                                  return [...prev];
                                });
                              }}
                            />
                          </div>
                          <div>
                            <div className="font-medium">Naar</div>
                            <AutocompletePostalcode
                              postalCodeName={`lines[${idx}].arrivalPostalCode`}
                              countryName={`lines[${idx}].arrivalCountry`}
                              cityName={`lines[${idx}].arrivalCity`}
                              onAutocomplete={(_postalCode, _city, longitude, latitude) => {
                                setLineStates((prev) => {
                                  prev[idx].arrival = [longitude, latitude];
                                  fetchRoute(prev[idx], idx).catch(console.error);
                                  return [...prev];
                                });
                              }}
                            />
                          </div>
                          <div className="2xl:pt-6">
                            <InputField labelText="Afstand (in km)" type="number" name={`lines[${idx}].distance`} />
                          </div>
                          <div className="2xl:pt-6">
                            <QuotationPriceInput
                              name={`lines[${idx}].price`}
                              quotationType={null}
                              departurePostalCode={values.lines[idx].departurePostalCode}
                              departureCountry={values.lines[idx].departureCountry.key}
                              arrivalPostalCode={values.lines[idx].arrivalPostalCode}
                              arrivalCountry={values.lines[idx].arrivalCountry.key}
                            />
                          </div>
                        </div>
                      </div>
                      <div className="max-w-6xl">
                        <TrailerTypes
                          value={values.lines[idx].trailerTypes}
                          onChange={(newTrailerTypes) => {
                            setFieldValue(`lines[${idx}].trailerTypes`, newTrailerTypes);
                          }}
                          isMultiSelect={true}
                          isRequired={true}
                          variant="small"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-fg-muted">Geen lijnen geëxtraheerd. Voeg handmatig lijnen toe.</div>
              )}
            </div>
          )}
        />
      </FormikProvider>
    </div>
  );
};
